/******************************************
 *  Author : vsuthar
 *  Created On : Thu Sep 02 2021
 *  File : Tree.js
 *  ©Palo Alto Networks
 *******************************************/

import { useMemo } from 'react'
import { Body, Button, TruncateText, noop } from '@panwds/react-ui'
import { HomeIcon, CaretRightIcon } from '@panwds/icons'
import { groupBy, keyBy } from 'lodash'
import TenantProductListing from '../../../../../tenantSwitcher/components/products/TenantProductListing'
import { LABEL_ALL_TENANTS, LABEL_PRODUCT_SERVICES, LABEL_TENANTS } from '../../../../../tenancy/constants/LabelConstants'
import { ASC_TIER_1_SUPPORT_ROLE } from '../../../../constants/GlobalConstants'
import { ROOT_NODE_ID } from '../../../../../tenancy/constants/TenancyConstants'
import { getVarValue } from '../../../../utils/Sparky'
import { FF_SHOW_LICENSED_PRODUCTS } from '../../../../utils/FeatureFlags'

const ffShowLicensedProducts = getVarValue(FF_SHOW_LICENSED_PRODUCTS, true)

/******************************************
 *  Author : vsuthar
 *  Created On : Wed Aug 18 2021
 *  File : index.js
 *  ©Palo Alto Networks
 *******************************************/



/*
const TreeSideBar = ({ title, children, style }) => {
  return <>
    <div className="tw-flex tw-flex-row tw-items-center tw-border-gray-200">
      {title}
    </div>
    <div className='tw-flex tw-flex-1 tw-flex-col tw-overflow-auto'>
      {children}
    </div>
  </>
}
*/


/**
 * This component used for render Tenant Switcher Tree.
 * Figma: https://www.figma.com/file/EymJVfGDnqtwh8ZMjSdaIq/End-to-End?node-id=0%3A1
 * @param treeData
 * @param selectedTreeNode
 * @param tenantSearchInput
 * @param treeKey
 * @param actions
 * @param isOpen
 * @param handleTreeNodeClick
 * @param handleInstanceSelection
 * @returns {JSX.Element}
 * @constructor
 */
const TreeView = ({
  treeData,
  selectedTreeNode,
  treeKey,
  actions,
  globalDispatch,
  isOpen,
  tsgAccess,
  handleTreeNodeClick,
  handleInstanceSelection,
  childTenantInstances,
  isTenantSwitcherLoading,
  isIPRestrictedError,
}) => {
  const { tree, keysSelected } = useMemo(() => {
    return {
      //TODO parent_id and id can be passed from parent. Data who dont have parent_id and id they needs to modify data.
      tree: groupBy(treeData, 'parent_id'),
      keysSelected: keyBy(treeData, 'id'),
    }
  }, [treeData])

  const showLicensedProducts = useMemo(() => (
    ffShowLicensedProducts && selectedTreeNode?.id !== ROOT_NODE_ID && !tsgAccess?.hasRoleOnly(ASC_TIER_1_SUPPORT_ROLE)
  ), [selectedTreeNode?.id, tsgAccess])

  const getSideBarHeader = useMemo(() => {
    return !selectedTreeNode?.parent_id ? (
      <div className='tsg-tree-view tw-flex tw-items-center'>
        <Button
          aria-label='home'
          appearance='tertiary-clear'
          onClick={noop}
          icon={<HomeIcon size='sm' className='tw-text-white mr-2' />}
          addClassName={
            'tw-flex tw-flex-row tw-bg-blue-steel-700 tw-border-blue-steel-700 dark:cm-bg-black dark:cm-border-black tw-pl-3 ' +
            'focus:tw-outline-none dark:focus:tw-bg-gray-800 hover:tw-bg-blue-steel-800 tw-border-l-4 ' +
            'hover:tw-border-blue-400 dark:hover:tw-bg-gray-800 hover:tw-border-l-4 active:tw-border-blue-400 dark:active:tw-bg-gray-700 active:tw-border-l-4'
          }
          style={{ cursor: 'default' }}
          size={'lg'}
        />
        <Body size="sm" appearance='secondary' addClassName='tw-text-gray-400 tw-py-2 tw-pl-3 tw-float-left'>{LABEL_ALL_TENANTS}</Body>
      </div>
    ) : (
      <div className='tsg-tree-view tw-flex tw-items-center'>
        <Button
          aria-label='home'
          appearance='tertiary-clear'
          onClick={handleTreeNodeClick.bind(this, {
            id: '_superRoot',
          })}
          icon={<HomeIcon size='sm' className='tw-text-white mr-2' />}
          addClassName={
            'tw-flex tw-flex-row tw-bg-blue-steel-700 tw-border-blue-steel-700 dark:cm-bg-black dark:cm-border-black tw-pl-3 ' +
            'focus:tw-outline-none hover:tw-bg-blue-steel-800 tw-border-l-4 ' +
            'hover:tw-border-blue-400 hover:tw-border-l-4 active:tw-border-blue-400 active:tw-border-l-4'
          }
          size={'lg'}
        />
        {selectedTreeNode?.parent_id !== '_superRoot' && (
          <Button
            appearance='clear'
            onClick={handleTreeNodeClick.bind(this, {
              id: selectedTreeNode?.parent_id,
              node: keysSelected[selectedTreeNode?.parent_id],
            })}
            style={{ color: '#fff' }}
            addClassName={
              'tw-flex tw-flex-row tw-cursor-pointer tw-bg-blue-steel-700' +
              'focus:tw-outline-none hover:tw-bg-blue-steel-800'
            }
            size={'lg'}>...</Button>
        )}
        <div className={`tw-flex tw-items-center tw-overflow-hidden tw-flex-grow ${selectedTreeNode?.parent_id === '_superRoot' ? 'tw-pl-3' : ''}`}>
          <div className='tw-pr-3 cm-leading-3'><CaretRightIcon size='xxs' className='tw-text-white mr-2' /></div>
          <Body addClassName='tsg-tree-current-tsg tw-flex tw-text-white tw-border-0 tw-m-0 tw-overflow-hidden tw-w-fit' appearance='primary' size='md'>
            <TruncateText>{selectedTreeNode?.display_name}</TruncateText>
          </Body>
        </div>
      </div>
    )
  }, [
    selectedTreeNode,
    handleTreeNodeClick,
    keysSelected,
  ])
  return (
    <>
      <div className="tw-text-white tw-flex tw-flex-col tw-justify-start tw-pt-2 " >
        <div style={{ minHeight: '35px' }}>{getSideBarHeader}</div>
        <div>
          {tree[selectedTreeNode?.id]?.length && selectedTreeNode?.parent_id && (
            <Body size="sm" appearance='secondary' addClassName='tw-text-gray-400 tw-py-2 tw-pl-3'>
              {LABEL_TENANTS}
            </Body>
          )}
        </div>
      </div>
      <div className="tw-text-white tw-flex tw-flex-col tw-overflow-auto nav-scrollbar">
        {tree[selectedTreeNode?.id]?.map((each) => {
          if (!each?.id) {
            return null
          }
          return (
            <div className="tw-flex tw-flex-col" key={each.id}>
              <Button
                data-tsg-id={each.id}
                appearance='tertiary-clear'
                fullWidth
                style={{ borderRadius: '0px !important' }}
                onClick={handleTreeNodeClick.bind(this, {
                  id: each.id,
                  node: keysSelected[each.id],
                })}
                addClassName={'tw-flex tw-flex-row tw-bg-blue-steel-700 tw-border-blue-steel-700 dark:cm-bg-black dark:cm-border-black tw-pl-3 ' +
                  'focus:tw-outline-none hover:tw-bg-blue-steel-800 tw-border-l-4 ' +
                  'dark:hover:tw-bg-gray-800 dark:focus:tw-bg-gray-800 dark:active:tw-bg-gray-700 ' +
                  'hover:tw-border-blue-400 hover:tw-border-l-4 focus:tw-border-l-4 focus:tw-border-blue-400'}
                size={'lg'}>
                <Body
                  size='md'
                  appearance='primary'
                  style={{ width: '255px' }}
                  addClassName='tw-flex tw-text-white tw-text-left tw-border-0 tw-color-primary'
                >
                  <TruncateText>{each.display_name}</TruncateText>
                </Body>
                {tree[each.id]?.length && (
                  <CaretRightIcon size='xxs' className='tw-text-white' />
                )}
              </Button>
            </div>
          )
        })}
      </div>
      {showLicensedProducts && <div className="tw-text-white tw-flex tw-flex-col tw-justify-start tw-pt-2 " >
        <div>
          <hr className='tw-mr-1 tw-ml-1 tw-mt-4 tw-mb-4 tw-border-solid tw-border-opacity-10 tw-border-black tw-border-b-0' />
          <Body size="sm" appearance='secondary' addClassName='tw-text-gray-400 tw-py-2 tw-pl-3'>
            {LABEL_PRODUCT_SERVICES}
          </Body>
        </div>
        <div className='tw-overflow-auto'>
          <TenantProductListing
            isTenantSwitcherLoading={isTenantSwitcherLoading}
            globalDispatch={globalDispatch}
            tenantProducts={childTenantInstances}
            isIPRestrictedError={isIPRestrictedError}
            color='tw-bg-blue-steel-700 tw-border-blue-steel-700 dark:cm-bg-black dark:cm-border-black'
          />
        </div>
      </div>}
    </>

  )
}

export default TreeView
